export interface Coordinates {
  lat: string;
  lng: string;
}

export type ViewerLocationMode = 'gps' | 'ip' | 'fixed';
export type ViewerLocationSource = 'fixed' | 'gps' | 'ip' | 'zurich';

export interface ViewerLocationState {
  mode: ViewerLocationMode;
  address: string;
  lat: string;
  lng: string;
}

export interface ResolvedViewerLocation {
  coords: Coordinates;
  source: ViewerLocationSource;
  label: string;
}

export const VIEWER_LOCATION_KEY = 'visime_viewer_location';
export const VIEWER_LOCATION_CHANGED = 'visime_viewer_location_changed';

/** Bahnhofstrasse, Zürich — same as iOS `UserLocalStore.defaultSignUpLatitude/Longitude`. */
export const DEFAULT_VIEWER_LATITUDE = 47.3696;
export const DEFAULT_VIEWER_LONGITUDE = 8.5386;
export const DEFAULT_VIEWER_ADDRESS = 'Bahnhofstrasse, Zürich';

export function defaultZurichCoordinates(): Coordinates {
  return {
    lat: String(DEFAULT_VIEWER_LATITUDE),
    lng: String(DEFAULT_VIEWER_LONGITUDE),
  };
}

export function isPlausibleCoordinates(
  lat: string | number | undefined | null,
  lng: string | number | undefined | null
): boolean {
  const la = typeof lat === 'string' ? parseFloat(lat) : lat;
  const ln = typeof lng === 'string' ? parseFloat(lng) : lng;
  if (la == null || ln == null || Number.isNaN(la) || Number.isNaN(ln)) {
    return false;
  }
  return Math.abs(la) > 1e-4 || Math.abs(ln) > 1e-4;
}

/** Prefer live/cached coords; otherwise iOS-matching Zurich fallback (never 0,0). */
export function resolveViewerCoordinates(coords?: Coordinates | null): Coordinates {
  if (coords && isPlausibleCoordinates(coords.lat, coords.lng)) {
    return coords;
  }
  return defaultZurichCoordinates();
}

export function defaultViewerLocationState(): ViewerLocationState {
  return {
    mode: 'gps',
    address: DEFAULT_VIEWER_ADDRESS,
    lat: String(DEFAULT_VIEWER_LATITUDE),
    lng: String(DEFAULT_VIEWER_LONGITUDE),
  };
}

export function loadViewerLocationState(): ViewerLocationState {
  const fallback = defaultViewerLocationState();
  try {
    if (typeof localStorage === 'undefined') return fallback;
    const raw = localStorage.getItem(VIEWER_LOCATION_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ViewerLocationState>;
    const mode =
      parsed.mode === 'gps' || parsed.mode === 'ip' || parsed.mode === 'fixed'
        ? parsed.mode
        : 'gps';
    const lat = typeof parsed.lat === 'string' ? parsed.lat : fallback.lat;
    const lng = typeof parsed.lng === 'string' ? parsed.lng : fallback.lng;
    const address =
      typeof parsed.address === 'string' && parsed.address.trim()
        ? parsed.address
        : fallback.address;
    return { mode, address, lat, lng };
  } catch {
    return fallback;
  }
}

export function saveViewerLocationState(state: ViewerLocationState): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(VIEWER_LOCATION_KEY, JSON.stringify(state));
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(VIEWER_LOCATION_CHANGED));
  }
}

export function setViewerLocationMode(mode: ViewerLocationMode): void {
  const current = loadViewerLocationState();
  saveViewerLocationState({ ...current, mode });
}

export function setFixedViewerLocation(address: string, lat: string, lng: string): void {
  saveViewerLocationState({
    mode: 'fixed',
    address: address.trim() || DEFAULT_VIEWER_ADDRESS,
    lat,
    lng,
  });
}

/**
 * Reads the device position once, giving up after `timeoutMs`.
 *
 * Desktop browsers can leave the permission prompt unanswered indefinitely, so
 * callers must never block content on this resolving with coordinates.
 */
export function requestLocationOnce(
  geolocation: Geolocation | undefined,
  timeoutMs: number
): Promise<Coordinates | null> {
  if (!geolocation) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;

    const settle = (value: Coordinates | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };

    const timer = setTimeout(() => settle(null), timeoutMs);

    try {
      geolocation.getCurrentPosition(
        (position) =>
          settle({
            lat: position.coords.latitude.toString(),
            lng: position.coords.longitude.toString(),
          }),
        () => settle(null),
        { timeout: timeoutMs, maximumAge: 300000 }
      );
    } catch {
      settle(null);
    }
  });
}

export interface ApproximateNetworkLocation extends Coordinates {
  label: string;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

function formatApproxLabel(city?: string | null, region?: string | null, country?: string | null): string {
  const parts = [city, region, country].map((p) => (p || '').trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'Approximate network location';
}

/**
 * City-level location from the public IP (ISP / network geo).
 * Not as precise as GPS; used when the user declines device location.
 */
export async function lookupIpApproximateLocation(
  fetchImpl: FetchLike = fetch
): Promise<ApproximateNetworkLocation | null> {
  const controllers: AbortController[] = [];

  const tryJson = async (url: string): Promise<Record<string, unknown> | null> => {
    const controller = new AbortController();
    controllers.push(controller);
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetchImpl(url, {
        method: 'GET',
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return (await res.json()) as Record<string, unknown>;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  const fromIpWho = await tryJson('https://ipwho.is/');
  if (fromIpWho && fromIpWho.success !== false) {
    const lat = fromIpWho.latitude;
    const lng = fromIpWho.longitude;
    if (isPlausibleCoordinates(lat as number, lng as number)) {
      return {
        lat: String(lat),
        lng: String(lng),
        label: formatApproxLabel(
          fromIpWho.city as string,
          fromIpWho.region as string,
          fromIpWho.country as string
        ),
      };
    }
  }

  const fromIpApi = await tryJson('https://ipapi.co/json/');
  if (fromIpApi && !fromIpApi.error) {
    const lat = fromIpApi.latitude;
    const lng = fromIpApi.longitude;
    if (isPlausibleCoordinates(lat as number, lng as number)) {
      return {
        lat: String(lat),
        lng: String(lng),
        label: formatApproxLabel(
          fromIpApi.city as string,
          fromIpApi.region as string,
          fromIpApi.country_name as string
        ),
      };
    }
  }

  return null;
}

export interface PlaceSearchResult {
  label: string;
  lat: string;
  lng: string;
}

/** OpenStreetMap Nominatim place search (no API key; city/address picker). */
export async function searchPlaces(
  query: string,
  fetchImpl: FetchLike = fetch
): Promise<PlaceSearchResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const url =
    'https://nominatim.openstreetmap.org/search?' +
    new URLSearchParams({
      q,
      format: 'json',
      addressdetails: '0',
      limit: '6',
    }).toString();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetchImpl(url, {
      method: 'GET',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return [];
    const data = (await res.json()) as Array<{ display_name?: string; lat?: string; lon?: string }>;
    if (!Array.isArray(data)) return [];
    return data
      .filter((row) => row.lat && row.lon && row.display_name)
      .map((row) => ({
        label: row.display_name as string,
        lat: String(row.lat),
        lng: String(row.lon),
      }));
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve viewer coordinates for Home/feed distance — iOS parity + IP fallback.
 *
 * fixed → saved address
 * gps → device GPS, then IP approx, then Zurich
 * ip → IP approx, then Zurich
 */
export async function resolveHomeViewerLocation(options?: {
  geolocation?: Geolocation;
  timeoutMs?: number;
  state?: ViewerLocationState;
  fetchImpl?: FetchLike;
}): Promise<ResolvedViewerLocation> {
  const state = options?.state ?? loadViewerLocationState();
  const timeoutMs = options?.timeoutMs ?? 8000;
  const fetchImpl = options?.fetchImpl ?? fetch;
  const zurich: ResolvedViewerLocation = {
    coords: defaultZurichCoordinates(),
    source: 'zurich',
    label: DEFAULT_VIEWER_ADDRESS,
  };

  if (state.mode === 'fixed' && isPlausibleCoordinates(state.lat, state.lng)) {
    return {
      coords: { lat: state.lat, lng: state.lng },
      source: 'fixed',
      label: state.address || DEFAULT_VIEWER_ADDRESS,
    };
  }

  if (state.mode === 'ip') {
    const approx = await lookupIpApproximateLocation(fetchImpl);
    if (approx) {
      return { coords: { lat: approx.lat, lng: approx.lng }, source: 'ip', label: approx.label };
    }
    return zurich;
  }

  // mode === 'gps' (default): try device, then network approx
  const gps = await requestLocationOnce(options?.geolocation ?? navigator.geolocation, timeoutMs);
  if (gps) {
    return { coords: gps, source: 'gps', label: 'Current location' };
  }

  const approx = await lookupIpApproximateLocation(fetchImpl);
  if (approx) {
    return { coords: { lat: approx.lat, lng: approx.lng }, source: 'ip', label: approx.label };
  }

  return zurich;
}
